import { useGraphDocumentData } from "@features/builder/GraphDocumentProvider/hooks/useGraphDocumentData"
import { useNodesValidation } from "@features/builder/graph/nodes/validation/useNodesValidation"
import { BulkGraphTagsFieldRow } from "@features/builder/BulkNodesEditDialog/BulkNodesEditForm/BulkGraphTagsFieldRow"
import { BulkNodesEditFormValues } from "@features/builder/BulkNodesEditDialog/BulkNodesEditForm/BulkNodesEditForm"
import { getMissingRequiredTagErrors } from "@features/builder/NodeDrawer/NodeForm/helpers"
import { GraphDetails } from "@features/builder/types"
import { Add } from "@mui/icons-material"
import { FormHelperText, Grid, IconButton, Tooltip } from "@mui/material"
import { CUSTOM_ERROR_ATTRIBUTE } from "@utils/form"
import { useMemo } from "react"
import { useFieldArray, useFormContext, useFormState } from "react-hook-form"

type GraphTagsCellProps = {
  nodeId: string
}

const GraphTagsCell = (props: GraphTagsCellProps) => {
  const form = useFormContext<BulkNodesEditFormValues>()
  const graphTagsPath = `nodes.${props.nodeId}.graphTags` as const
  const { fields, append, remove } = useFieldArray({ control: form.control, name: graphTagsPath })
  const { errors } = useFormState({ control: form.control, name: graphTagsPath })
  const errorMessage = errors.nodes?.[props.nodeId]?.graphTags?.root?.message ?? ""

  const { getNodeValidation } = useNodesValidation()
  const validationErrors = getNodeValidation(props.nodeId, "GRAPH_TAGS")?.errors ?? []
  const missingRequiredTagErrors = getMissingRequiredTagErrors(validationErrors)

  const graphDocumentData = useGraphDocumentData()
  const graphData: GraphDetails | null = useMemo(() => {
    try {
      return JSON.parse(graphDocumentData.data)
    } catch {
      return null
    }
  }, [graphDocumentData.data])

  const graphTagsMap = useMemo(() => {
    const map = new Map<string, string[]>()
    ;(graphData?.GraphTags ?? []).forEach((tag) => {
      map.set(tag.Name, [...(map.get(tag.Name) ?? []), tag.Value])
    })
    return map
  }, [graphData])

  return (
    <Grid container direction="column" spacing={1} wrap="nowrap">
      {fields.map((tagField, index) => (
        <BulkGraphTagsFieldRow
          key={tagField.id}
          nodeId={props.nodeId}
          index={index}
          onDelete={() => remove(index)}
          graphTagsMap={graphTagsMap}
          validationErrors={validationErrors}
        />
      ))}

      {missingRequiredTagErrors.map((error, index) => (
        <Grid item key={index}>
          <FormHelperText error>{error.msg}</FormHelperText>
        </Grid>
      ))}

      {errorMessage && (
        <Grid item>
          <FormHelperText error {...{ [CUSTOM_ERROR_ATTRIBUTE]: true }}>
            {errorMessage}
          </FormHelperText>
        </Grid>
      )}

      <Grid item>
        <Tooltip title="Add Tag">
          <span>
            <IconButton
              color="primary"
              size="small"
              disabled={form.formState.disabled}
              onClick={() => append({ tag: "", values: [] })}
            >
              <Add />
            </IconButton>
          </span>
        </Tooltip>
      </Grid>
    </Grid>
  )
}

export default GraphTagsCell
